import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { fetchWithCsrf } from "@/ui/client/fetchWithCsrf";
import { useNavigate, useParams } from "@/spa/routerCompat";
import { FetchJsonError, fetchJson } from "../api";

type InviteData = {
  projectName: string;
  role: "editor" | "viewer";
  expiresAt: string;
};

export const ProjectInviteRoute = () => {
  const { t } = useTranslation();
  const { token } = useParams<{ token: string }>();
  const navigate = useNavigate();
  const [invite, setInvite] = useState<InviteData | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!token) return;
    fetchJson<InviteData>(`/api/project-invites/${encodeURIComponent(token)}`)
      .then(setInvite)
      .catch((cause: unknown) => {
        setError(
          cause instanceof FetchJsonError && cause.status === 404
            ? t("projectInvite.notFound")
            : cause instanceof Error ? cause.message : t("projectInvite.loadFailed")
        );
      });
  }, [t, token]);

  const accept = async (): Promise<void> => {
    if (!token) return;
    setBusy(true);
    try {
      const response = await fetchWithCsrf(`/api/project-invites/${encodeURIComponent(token)}`, { method: "POST" });
      const body = await response.json().catch(() => ({})) as { projectId?: string; error?: string };
      if (!response.ok || !body.projectId) throw new Error(body.error || t("projectInvite.acceptFailed"));
      navigate(`/projects/${body.projectId}`);
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : t("projectInvite.acceptFailed"));
      setBusy(false);
    }
  };

  return (
    <div className="mx-auto max-w-lg py-12">
      <Card>
        <CardHeader>
          <CardTitle>{t("projectInvite.heading")}</CardTitle>
          <CardDescription>
            {invite
              ? t("projectInvite.description", { project: invite.projectName, role: t(`projectSettings.members.role${invite.role === "editor" ? "Editor" : "Viewer"}`) })
              : error || t("common.loading")}
          </CardDescription>
        </CardHeader>
        {invite ? (
          <CardContent>
            <Button disabled={busy} onClick={() => void accept()}>
              {busy ? t("projectInvite.accepting") : t("projectInvite.accept")}
            </Button>
          </CardContent>
        ) : null}
      </Card>
    </div>
  );
};
